import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Link,
  useNavigate,
  useSearchParams,
  useParams,
  generatePath, // added
} from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { adminRoutes } from "../../../routers/AdminRouters";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import {
  useGetFormatsQuery,
  useCreateSessionFormatMutation,
  useGetSessionFormatQuery,
} from "../../../services";
import DeleteBlockModal from "./modals/DeleteBlockModal";

const SessionFormatBuilder: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("category");
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [orderedFormats, setOrderedFormats] = useState<string[]>([]);
  const { sessionid } = useParams<{ sessionid?: string }>();
  const [createSessionFormat, { isLoading: isSaving }] =
    useCreateSessionFormatMutation();
  const [updateSessionFormat, { isLoading: isUpdating }] =
    useCreateSessionFormatMutation();
  const { data: formatsData, isLoading: isFormatsLoading } = useGetFormatsQuery(
    categoryId || ""
  );
  const { data: sessionFormatData, isLoading: isSessionFormatLoading } =
    useGetSessionFormatQuery(sessionid ?? "", { skip: !sessionid });
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const [hasExistingFormats, setHasExistingFormats] = useState(false);
  // Snapshot of formats as they were loaded from the server (to detect changes)
  const [originalFormats, setOriginalFormats] = useState<string[]>([]);
  const themeBox = theme === "dark";

  // Track if editingz
  const [isEditMode, setIsEditMode] = useState(false);

  // Confirmation Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formatToDelete, setFormatToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  useEffect(() => {
    const results = sessionFormatData?.results;
    if (results && Array.isArray(results) && results.length > 0) {
      setIsEditMode(true);
      const sortedResults = [...results].sort(
        (a: any, b: any) => Number(a.order ?? 0) - Number(b.order ?? 0)
      );
      // Normalize to string IDs. format may be object or string.
      const ordered = sortedResults.map((f: any) => {
        const fmt = f.format;
        return typeof fmt === "object" && fmt?._id
          ? fmt._id
          : String(fmt ?? f._id);
      });
      setOrderedFormats(ordered);
      setSelectedFormats(ordered);
      setOriginalFormats(ordered); // save snapshot for change detection
      setHasExistingFormats(true);
    }
  }, [sessionFormatData, sessionid]);

  const handleFormatToggle = (formatId: string) => {
    if (selectedFormats.includes(formatId)) {
      setSelectedFormats((prev) => prev.filter((f) => f !== formatId));
      setOrderedFormats((prev) => prev.filter((f) => f !== formatId));
    } else {
      setSelectedFormats((prev) => [...prev, formatId]);
      setOrderedFormats((prev) => [...prev, formatId]);
    }
  };

  const removeFromOrdered = (formatId: string) => {
    const formatObj = formatsData?.find((f: any) => f._id === formatId);
    setFormatToDelete({
      id: formatId,
      name: formatObj?.name || "Format",
    });
    setShowDeleteModal(true);
  };

  const confirmRemove = () => {
    if (!formatToDelete) return;
    const formatId = formatToDelete.id;
    setSelectedFormats((prev) => prev.filter((f) => f !== formatId));
    setOrderedFormats((prev) => prev.filter((f) => f !== formatId));
    setShowDeleteModal(false);
    setFormatToDelete(null);
  };

  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  const scrollColors = useMemo(
    () =>
      theme === "dark"
        ? { track: "#111111", thumb: "#2A2A2A" }
        : { track: "#F5F7FA", thumb: "#C9CED6" },
    [theme]
  );

  // Drag handlers
  const handleDragStart = (index: number) => {
    dragItem.current = index;
    setDragIndex(index);
  };

  const handleDragEnter = (index: number) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    const from = dragItem.current;
    const to = dragOverItem.current;
    if (from === null || to === null || from === to) {
      setDragIndex(null);
      return;
    }
    const updated = [...orderedFormats];
    const [removed] = updated.splice(from, 1);
    updated.splice(to, 0, removed);
    setOrderedFormats(updated);
    setDragIndex(null);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleSaveNext = async () => {
    if (orderedFormats.length === 0 || !sessionid) return;

    // Check if the formats have changed compared to the original snapshot
    const hasChanged =
      !isEditMode ||
      orderedFormats.length !== originalFormats.length ||
      orderedFormats.some((id, idx) => id !== originalFormats[idx]);

    const pathTemplate =
      (adminRoutes as any).sessionBlocksBuilder || "/session/blocks-builder";
    const path = generatePath(pathTemplate, { sessionid });
    const search = categoryId
      ? `?category=${encodeURIComponent(categoryId)}`
      : "";

    if (!hasChanged) {
      // Nothing changed – navigate directly without calling the API
      navigate(`${path}${search}`);
      return;
    }

    const orderedData = orderedFormats.map((id, idx) => ({
      format: id,
      order: idx + 1,
    }));
    try {
      if (isEditMode) {
        await updateSessionFormat({ sessionid, formats: orderedData }).unwrap();
      } else {
        await createSessionFormat({ sessionid, formats: orderedData }).unwrap();
      }
      navigate(`${path}${search}`);
    } catch (err) {
      console.error("Failed to save formats", err);
    }
  };

  // Remove full-page loading guard to prevent black screen flicker

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-6 pb-8 min-h-screen ${theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50"
        }`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: var(--scroll-track);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: var(--scroll-thumb);
          border-radius: 10px;
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: var(--scroll-thumb) var(--scroll-track);
        }
      `}</style>
      {/* Header */}
      <div className="mb-4 mt-12">
        <h1
          className={`text-xl font-semibold ${theme === "dark" ? "text-gray-300" : "text-gray-800"
            }`}
        >
          {" "}
          Session Overview
        </h1>
        <div className="flex items-center gap-2 mt-2 text-sm">
          <Link
            to={`${adminRoutes.sessionList}`}
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            All Sessions
          </Link>
          <span
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            /
          </span>
          <span
            className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}
          >
            {hasExistingFormats ? "Update Session " : "Create New Session "}
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`rounded-lg ${theme === "dark" ? "bg-[#1A1A1A]" : "bg-white"
          } border ${theme === "dark" ? "border-[#333]" : "border-gray-200"} overflow-hidden`}
      >
        <div className="flex">
          {/* Left Sidebar */}
          <div
            className={`flex flex-col items-center justify-center min-w-[180px] max-w-[220px] border-r ${theme === "dark"
              ? "bg-[#181818] border-[#232323]"
              : "bg-gray-100 border-gray-300"
              }`}
          >
            <div className="flex flex-col items-center">
              <div
                className={`rounded-xl w-16 h-16 flex items-center justify-center mb-3 ${theme === "dark"
                  ? "bg-[#232323]"
                  : "bg-white border border-gray-200"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="36"
                  height="36"
                  viewBox="0 0 36 36"
                  fill="none"
                >
                  <path
                    d="M16.5 31.5H15C9.34314 31.5 6.51473 31.5 4.75736 29.7426C3 27.9853 3 25.1568 3 19.5V15C3 9.34314 3 6.51473 4.75736 4.75736C6.51473 3 9.34314 3 15 3H18C23.6568 3 26.4853 3 28.2426 4.75736C30 6.51473 30 9.34314 30 15V15.75"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M26.1103 21.6054C26.4288 20.7982 27.5712 20.7982 27.8896 21.6054L27.9447 21.7453C28.7223 23.7169 30.283 25.2777 32.2546 26.0553L32.3946 26.1103C33.2017 26.4288 33.2017 27.5712 32.3946 27.8896L32.2546 27.9447C30.283 28.7223 28.7223 30.283 27.9447 32.2546L27.8896 32.3946C27.5712 33.2017 26.4288 33.2017 26.1103 32.3946L26.0553 32.2546C25.2777 30.283 23.7169 28.7223 21.7453 27.9447L21.6054 27.8896C20.7982 27.5712 20.7982 26.4288 21.6054 26.1103L21.7453 26.0553C23.7169 25.2777 25.2777 23.7169 26.0553 21.7453L26.1103 21.6054Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10.5 10.5H22.5M10.5 17.25H22.5M10.5 24H16.5"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span
                className={`text-sm font-medium text-center px-4 ${theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
              >
                Format will appear here
              </span>
            </div>
          </div>

          {/* Right Main Content area */}
          <div className="flex-1">
            {/* Progress Header */}
            <div className="p-3 border-b border-[#333]">
              <h2
                className={`text-lg font-medium mb-3 ${theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
              >
                {hasExistingFormats ? "Update Session" : "Create New Session"}
              </h2>
              <div className="w-full h-1 bg-[#333] rounded relative">
                <div
                  className="absolute left-0 top-0 h-1 bg-[#B6CB24] rounded"
                  style={{ width: "35%" }}
                />
              </div>
            </div>

            <div className="flex">
              {/* Left Panel - Choose Formats */}
              <div className="w-1/2 p-6 border-r border-[#333]">
                <h3
                  className={`text-base font-medium mb-4 ${theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                >
                  Choose Formats
                </h3>

                <div className="space-y-3">
                  {isFormatsLoading ? (
                    // Skeleton for formats
                    [1, 2, 3, 4].map((i) => (
                      <div key={i} className="flex items-center gap-3 animate-pulse">
                        <div className={`w-4 h-4 rounded ${theme === "dark" ? "bg-white/5" : "bg-gray-200"}`} />
                        <div className={`h-4 w-32 rounded ${theme === "dark" ? "bg-white/5" : "bg-gray-200"}`} />
                      </div>
                    ))
                  ) : (
                    formatsData?.map((format: any) => (
                      <label
                        key={format._id}
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={selectedFormats.includes(format._id)}
                            onChange={() => handleFormatToggle(format._id)}
                            className="sr-only"
                          />
                          <div
                            className={`w-4 h-4 rounded border ${
                              selectedFormats.includes(format._id)
                                ? "bg-[#B6CB24] border-[#B6CB24]"
                                : theme === "dark"
                                  ? "border-gray-600"
                                  : "border-gray-400"
                            } flex items-center justify-center`}
                          >
                            {selectedFormats.includes(format._id) && (
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                              >
                                <path
                                  d="M2.5 6L5 8.5L9.5 3.5"
                                  stroke="black"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                        </div>
                        <span
                          className={`text-sm ${
                            theme === "dark" ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {format.name}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Right Panel - Order the Format */}
              <div className="w-1/2 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3
                    className={`text-base font-medium ${theme === "dark" ? "text-white" : "text-gray-900"
                      }`}
                  >
                    Order the Format
                  </h3>
                  <div className="flex items-center gap-2">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M8 1.33333V14.6667"
                        stroke="#666"
                        strokeWidth="1.33333"
                        strokeLinecap="round"
                      />
                      <path
                        d="M14.6667 8H1.33333"
                        stroke="#666"
                        strokeWidth="1.33333"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
                {/* Format Preview Area */}
                <div
                  className={`min-h-[300px] max-h-[300px] overflow-y-auto rounded-xl border border-dashed ${theme === "dark" ? "border-[#444] bg-[#111111]" : "border-gray-300"
                    } p-4 relative custom-scrollbar`}
                  style={{
                    ["--scroll-track" as any]: scrollColors.track,
                    ["--scroll-thumb" as any]: scrollColors.thumb,
                  } as React.CSSProperties}
                >
                  {isSessionFormatLoading ? (
                    <div className="space-y-3">
                      {[1, 2].map((i) => (
                        <div
                          key={i}
                          className={`p-3.5 rounded-xl animate-pulse ${
                            theme === "dark" ? "bg-white/5" : "bg-gray-100"
                          } h-14`}
                        />
                      ))}
                    </div>
                  ) : orderedFormats.length === 0 ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div
                        className={`rounded-xl w-12 h-12 flex items-center justify-center mb-3 ${
                          theme === "dark" ? "bg-[#333]" : "bg-gray-100"
                        }`}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M12 2V22"
                            stroke="#B6CB24"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M22 12H2"
                            stroke="#B6CB24"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <span
                        className={`text-sm ${
                          theme === "dark" ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        No formats added yet. Add formats here to build your
                        session.
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {orderedFormats.map((formatId, index) => {
                        const formatObj = formatsData?.find(
                          (f: any) => f._id === formatId
                        );
                        if (!formatObj) return null;
                        return (
                          <div
                            key={formatObj._id}
                            className={`flex items-center justify-between p-3.5 rounded-xl ${
                              theme === "dark"
                                ? "bg-[#222222] hover:bg-[#282828]"
                                : "bg-white border border-gray-100"
                            } transition-colors`}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragEnter={() => handleDragEnter(index)}
                            onDragEnd={handleDragEnd}
                            style={{
                              opacity: dragIndex === index ? 0.5 : 1,
                              cursor: "move",
                            }}
                          >
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M9 6H9.01M9 12H9.01M9 18H9.01M15 6H15.01M15 12H15.01M15 18H15.01"
                                    stroke={theme === "dark" ? "#888" : "#999"}
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                              <span
                                className={`text-base font-medium ${
                                  theme === "dark"
                                    ? "text-gray-200"
                                    : "text-gray-700"
                                }`}
                              >
                                {formatObj.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="4"
                                  y="4"
                                  width="16"
                                  height="16"
                                  rx="5"
                                  stroke={theme === "dark" ? "#F3F3F6" : "#666"}
                                  strokeWidth="1.2"
                                />
                                <path
                                  d="M12 16V11"
                                  stroke={theme === "dark" ? "#F3F3F6" : "#666"}
                                  strokeWidth="1.2"
                                  strokeLinecap="round"
                                />
                                <circle
                                  cx="12"
                                  cy="8"
                                  r="0.8"
                                  fill={theme === "dark" ? "#F3F3F6" : "#666"}
                                />
                              </svg>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeFromOrdered(formatObj._id);
                                }}
                                className="p-1 hover:bg-red-500/10 rounded-md transition-colors"
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M18 6L6 18M6 6L18 18"
                                    stroke={
                                      theme === "dark" ? "#F3F3F6" : "#666"
                                    }
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Buttons */}
            <div className="flex items-center justify-center gap-4 p-4 border-t border-[#333]">
              <button
                className={`flex items-center gap-2 px-6 py-2 rounded-lg bg-[#B6CB24] text-black font-medium  ${theme === "dark"
                  ? "bg-[#333333] text-gray-200 "
                  : "bg-gray-200 text-gray-800 "
                  } ${orderedFormats.length === 0
                    ? "opacity-50 pointer-events-none"
                    : ""
                  }
             `

                }
                style={{
                  background:
                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198,217,44,0.80) 0%, rgba(83,91,16,0.80) 100%)",
                }}

                disabled={orderedFormats.length === 0}
                onClick={handleSaveNext}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M19 21C19.6068 20.4102 22 18.8403 22 18C22 17.1597 19.6068 15.5898 19 15M21 18H14"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 21C7.28595 21 4.92893 21 3.46447 19.5355C2 18.0711 2 15.714 2 11V7.94427C2 6.1278 2 5.21956 2.38032 4.53806C2.65142 4.05227 3.05227 3.65142 3.53806 3.38032C4.21956 3 5.1278 3 6.94427 3C8.10802 3 8.6899 3 9.19926 3.19101C10.3622 3.62712 10.8418 4.68358 11.3666 5.73313L12 7M8 7H16.75C18.8567 7 19.91 7 20.6667 7.50559C20.9943 7.72447 21.2755 8.00572 21.4944 8.33329C21.9796 9.05942 21.9992 10.0588 22 12V13"
                    stroke="#F3F3F6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                {hasExistingFormats ? "Update & Next" : "Save & Next"}
              </button>

              <button
                className={`flex items-center gap-2 px-6 py-2 rounded-lg border ${theme === "dark"
                  ? "border-gray-600 text-gray-300 hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}

                onClick={() =>
                  navigate(
                    `${adminRoutes.sessionEdit.replace(
                      ":sessionid",
                      String(sessionid || "")
                    )}${categoryId
                      ? `?category=${encodeURIComponent(categoryId)}`
                      : ""
                    }`
                  )
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M11 6H15.5C17.9853 6 20 8.01472 20 10.5C20 12.9853 17.9853 15 15.5 15H4"
                    stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.99998 12C6.99998 12 4.00001 14.2095 4 15C3.99999 15.7906 7 18 7 18"
                    stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Previous
              </button>

              <button
                onClick={() => navigate(adminRoutes.sessionList)}
                className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium border ${themeBox
                  ? "border-[#444] text-gray-300 hover:bg-[#1E1E1E]"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 21.5C16.4783 21.5 18.7175 21.5 20.1088 20.1088C21.5 18.7175 21.5 16.4783 21.5 12C21.5 7.52165 21.5 5.28248 20.1088 3.89124C18.7175 2.5 16.4783 2.5 12 2.5C7.52166 2.5 5.28249 2.5 3.89124 3.89124C2.5 5.28249 2.5 7.52166 2.5 12C2.5 16.4783 2.5 18.7175 3.89124 20.1088C5.28248 21.5 7.52165 21.5 12 21.5Z"
                    stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15 9L9 14.9996M15 15L9 9.00039"
                    stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <DeleteBlockModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setFormatToDelete(null);
        }}
        themeBox={theme === "dark"}
        blockId={formatToDelete?.id || ""}
        blockName={formatToDelete?.name || ""}
        onConfirmDelete={confirmRemove}
      />
    </div>
  );
};

export default SessionFormatBuilder;
